import { createTheme } from '@mui/material/styles';
import { colors } from './colors';
import { tokens } from '../../styles/tokens';
import popoverBlurBlue from '../../assets/popover-blur-blue.png';
import popoverBlurRed from '../../assets/popover-blur-red.png';
import {
  alertIconMapping,
  autocompleteClearIcon,
  autocompletePopupIcon,
  ChevronDownIcon,
} from '../../app/common/Icon/muiIcons';

export const theme = createTheme({
  // Palette slots map to the design tokens so components styled by MUI itself
  // (Radio, Pagination, Alert icons, focus rings...) match the SCSS palette.
  palette: {
    primary: {
      main: tokens.color.brandPrimary,
      contrastText: tokens.color.textInverse,
    },
    error: { main: tokens.color.error },
    warning: { main: tokens.color.warning },
    success: { main: tokens.color.success },
    info: { main: tokens.color.info },
    text: {
      primary: tokens.color.text,
      secondary: tokens.color.textSecondary,
      disabled: tokens.color.textMuted,
    },
    divider: tokens.color.blue200,
    background: {
      default: tokens.color.neutral100,
      paper: tokens.color.neutral100,
    },
    grey: {
      50: tokens.color.surface1,
      100: tokens.color.surface2,
      200: tokens.color.neutral300,
      300: tokens.color.neutral300,
      400: tokens.color.neutral600,
      500: tokens.color.neutral600,
      600: tokens.color.neutral700,
      700: tokens.color.neutral700,
      800: tokens.color.neutral800,
      900: tokens.color.neutral900,
    },
  },
  typography: {
    fontFamily: ['Poppins', 'sans-serif'].join(','),
  },
  // MUI's default corner: the m token (Paper, Alert, Dialog, Menu, Accordion
  // ...). Fields, chips, menu rows and tooltips step down to s below. A number
  // because MUI does arithmetic on it (Skeleton).
  shape: {
    borderRadius: parseInt(tokens.radius.m, 10),
  },
  components: {
    // @ts-ignore
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: colors.pickerActiveBackgroundColor,

            '&:hover': {
              backgroundColor: colors.pickerActiveBackgroundHoverColor,
            },

            '&:focus': {
              backgroundColor: colors.pickerActiveBackgroundHoverColor,
            },
          },
          '&:hover': {
            backgroundColor: colors.pickerHoverColor,
          },
        },
      },
    },
    // @ts-ignore
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          '&.Mui-selected': {
            backgroundColor: colors.pickerActiveBackgroundColor,

            '&:hover': {
              backgroundColor: colors.pickerActiveBackgroundHoverColor,
            },

            '&:focus': {
              backgroundColor: colors.pickerActiveBackgroundHoverColor,
            },
          },
          '&:hover': {
            backgroundColor: colors.pickerHoverColor,
          },
        },
      },
    },
    // One field style for the whole site (design audit, section 4.2): a 44px
    // box with a blue400 border, a 2px brand-blue focus border, a static
    // label above the box (no floating label, so no outline notch), a muted
    // placeholder and the error in red under the field. Everything MUI builds
    // on OutlinedInput (TextField, Select, Autocomplete, DatePicker) inherits
    // it; the custom inputs mirror it with the `field-box` mixin in
    // src/styles/_mixins.scss - change both together.
    MuiInputLabel: {
      styleOverrides: {
        root: {
          position: 'static',
          transform: 'none',
          maxWidth: 'none',
          overflow: 'visible',
          whiteSpace: 'normal',
          pointerEvents: 'auto',
          marginBottom: '8px',
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '140%',
          color: colors.inputLabelColor,

          '&.Mui-focused': {
            color: colors.inputLabelColor,
          },
          '&.Mui-error': {
            color: colors.inputErrorColor,
          },
          '&.Mui-disabled': {
            color: colors.inputPlaceholderColor,
          },
          '& .MuiInputLabel-asterisk': {
            color: colors.inputFocusColor,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.s,
          backgroundColor: colors.backgroundColor,

          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.inputFocusColor,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.inputFocusColor,
            borderWidth: '2px',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.inputErrorColor,
          },
          '&.Mui-disabled': {
            backgroundColor: tokens.color.surface2,

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.color.blue200,
            },
          },
          '&.MuiInputBase-adornedStart': {
            paddingLeft: '16px',
          },
          '&.MuiInputBase-adornedEnd': {
            paddingRight: '12px',
          },
          '&.MuiInputBase-multiline': {
            padding: '12px 16px',
          },
        },
        // No label notch: MUI keeps the outline 5px above the box to leave
        // room for the floating label's legend.
        notchedOutline: {
          top: 0,
          borderColor: colors.inputBorderColor,

          '& legend': {
            display: 'none',
          },
        },
        input: {
          height: '20px',
          padding: '12px 16px',
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: '20px',

          '&::placeholder': {
            color: colors.inputPlaceholderColor,
            opacity: 1,
          },
          '&.MuiInputBase-inputAdornedStart': {
            paddingLeft: 0,
          },
          '&.MuiInputBase-inputAdornedEnd': {
            paddingRight: 0,
          },
          '&.MuiInputBase-inputMultiline': {
            height: 'auto',
            padding: 0,
          },
        },
      },
    },
    MuiSelect: {
      // The site's chevron instead of MUI's filled triangle (design audit,
      // section 4.6); MUI still rotates it through the MuiSelect-iconOpen
      // class.
      defaultProps: {
        IconComponent: ChevronDownIcon,
      },
      styleOverrides: {
        select: {
          // Same 44px box as a text field (MUI's own min-height is 23px).
          '&.MuiSelect-select': {
            minHeight: '20px',
          },
        },
        // MUI centres a 1em icon; ours is a 20px box.
        icon: {
          top: 'calc(50% - 10px)',
          color: colors.inputLabelColor,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: '8px 0 0',
          fontSize: '0.75rem',
          fontWeight: 400,
          lineHeight: '140%',
          letterSpacing: 'normal',

          '&.Mui-error': {
            color: colors.inputErrorColor,
          },
        },
      },
    },
    // Mirrors src/app/common/Button/Button.module.scss so a MUI Button used
    // directly (ErrorFallback, NewVersion...) looks like the custom one:
    // contained = primary, outlined = secondary, text = tertiary,
    // text + color="error" = danger.
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: '44px',
          padding: '10px 24px',
          gap: '8px',
          borderRadius: tokens.radius.m,
          fontSize: '1rem',
          fontWeight: 500,
          lineHeight: '120%',
          textTransform: 'none',
          boxShadow: 'none',

          '&:hover': {
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            opacity: 0.4,
          },
        },
        sizeSmall: {
          minHeight: '36px',
          padding: '6px 16px',
          fontSize: '0.875rem',
        },
        sizeLarge: {
          minHeight: '52px',
          padding: '14px 32px',
        },
        contained: {
          backgroundColor: colors.buttonPrimaryBackgroundColor,
          color: colors.buttonPrimaryColor,

          '&:hover': {
            backgroundColor: colors.buttonPrimaryBackgroundHoverColor,
          },
          '&.Mui-disabled': {
            backgroundColor: colors.buttonPrimaryBackgroundColor,
            color: colors.buttonPrimaryColor,
          },
        },
        outlined: {
          backgroundColor: colors.buttonSecondaryBackgroundColor,
          borderColor: colors.buttonSecondaryColor,
          color: colors.buttonSecondaryColor,

          '&:hover': {
            backgroundColor: colors.buttonSecondaryBackgroundHoverColor,
            borderColor: colors.buttonSecondaryColor,
          },
          '&.Mui-disabled': {
            borderColor: colors.buttonSecondaryColor,
            color: colors.buttonSecondaryColor,
          },
        },
        text: {
          paddingRight: 0,
          paddingLeft: 0,
          color: colors.buttonSecondaryColor,

          '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          },
          '&.MuiButton-colorError': {
            color: colors.buttonDangerColor,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          width: '100%',

          '::before': {
            borderTopStyle: 'dashed',
            borderTopWidth: 'thin',
            borderTopColor: colors.dividerBorderColor,
          },
          '::after': {
            borderTopStyle: 'dashed',
            borderTopWidth: 'thin',
            borderTopColor: colors.dividerBorderColor,
          },

          '& .MuiDivider-wrapper': {
            fontSize: '0.75rem',
            fontWeight: '700',
            color: colors.dividerColor,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: colors.progressBackgroundColor,

          '& .MuiLinearProgress-bar1Indeterminate': {
            backgroundColor: colors.progressColor,
          },

          '& .MuiLinearProgress-bar2Indeterminate': {
            backgroundColor: colors.progressColor,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            backgroundColor: colors.progressColor,
          },

          '& .MuiTab-textColorPrimary': {
            color: colors.tabDefaultColor,
            fontWeight: '600',
          },

          '& .Mui-selected': {
            color: 'inherit !important',
            fontWeight: '600',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: colors.tableHeadBackgroundColor,
          fontWeight: '600',
          fontSize: '14px',
          color: colors.tableHeadColor,
          borderBottomColor: colors.tableLineBorderColor,
        },
        body: {
          color: colors.textColor,
          lineHeight: '1.57143',
          fontSize: '0.875rem',
          fontWeight: '400',
          borderBottomColor: colors.tableLineBorderColor,
        },
        paddingCheckbox: {
          padding: '0 0 0 8px',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '0',
          paddingRight: '8px',
          color: colors.checkboxColor,

          '&.Mui-checked': {
            color: colors.checkboxColor,
          },

          '&.MuiCheckbox-indeterminate': {
            color: colors.checkboxColor,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          '& .MuiCheckbox-root + .MuiTypography-root': {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '130%',
            color: colors.checkboxLabelColor,
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h4: {
          letterSpacing: 'normal',
        },
      },
    },
    MuiAlert: {
      // Status icons from the site's set (design audit, section 4.6)
      defaultProps: {
        iconMapping: alertIconMapping,
      },
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.m,
          lineHeight: 1.57143,

          '&.MuiAlert-colorError': {
            backgroundColor: colors.alertErrorBackgroundColor,
            color: colors.alertErrorColor,
          },

          '&.MuiAlert-colorWarning': {
            backgroundColor: colors.alertWarningBackgroundColor,
            color: colors.alertWarningColor,
          },

          '&.MuiAlert-colorSuccess': {
            backgroundColor: colors.alertSuccessBackgroundColor,
            color: colors.alertSuccessColor,
          },
        },
      },
    },
    // Floating surfaces - menus, popovers, dialogs, autocomplete and date
    // picker lists: the m radius and the floating shadow (design audit 4.4).
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.m,
          color: colors.textColor,
          backdropFilter: 'blur(20px)',
          backgroundImage: `url(${popoverBlurBlue}), url(${popoverBlurRed})`,
          backgroundRepeat: 'no-repeat, no-repeat',
          backgroundPosition: 'right top, left bottom',
          backgroundSize: '50% 50%',
          boxShadow: tokens.shadow[2],
          width: '200px',
          overflow: 'inherit',
          padding: '0',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: '400',
          padding: '6px 8px',
          borderRadius: tokens.radius.s,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.m,
          boxShadow: tokens.shadow[2],
        },
      },
    },
    // @ts-ignore
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          borderRadius: tokens.radius.m,
          boxShadow: tokens.shadow[2],
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        sizeSmall: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          paddingLeft: '8px',
          paddingRight: '8px',
          whiteSpace: 'nowrap',
          fontWeight: '500',
          borderRadius: tokens.radius.s,

          '&:hover': {
            backgroundColor: colors.tableLineHoverColor,
          },
        },
      },
    },
    MuiAutocomplete: {
      // Same chevron as the Select, the site's "X" to clear
      defaultProps: {
        popupIcon: autocompletePopupIcon,
        clearIcon: autocompleteClearIcon,
      },
      styleOverrides: {
        popper: {
          zIndex: '10000',
        },
        popupIndicator: {
          color: colors.inputLabelColor,
        },
        clearIndicator: {
          color: colors.inputLabelColor,
        },
        paper: {
          backgroundImage: `url('../../assets/popover-blur-blue.png'), url('../../assets/popover-blur-red.png')`,
          backgroundRepeat: `no-repeat, no-repeat`,
          backgroundSize: `50%, 50%`,
          backgroundPosition: `right top, left bottom`,
          backdropFilter: `blur(20px)`,
          borderRadius: tokens.radius.m,
          boxShadow: tokens.shadow[2],
        },
        listbox: {
          padding: '8px',

          '.MuiAutocomplete-option': {
            borderRadius: tokens.radius.s,
            padding: '6px 8px',
          },
        },
        inputRoot: {
          padding: '12px 39px 12px 16px',

          '& .MuiInputBase-input': {
            padding: '0',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          marginLeft: '6px',

          '&.Mui-checked': {
            marginLeft: '0',
          },
        },

        thumb: {
          color: colors.switcherThumbColor,
          width: '14px',
          height: '14px',
        },

        track: {
          height: '20px',
          backgroundColor: colors.switcherTrackColor,
          borderRadius: tokens.radius.pill,
          opacity: '1',

          '.Mui-checked.Mui-checked + &': {
            opacity: 1,
            backgroundColor: colors.switcherColor,
          },
        },
      },
    },
    MuiTablePagination: {
      defaultProps: {
        slotProps: {
          select: {
            MenuProps: {
              slotProps: {
                paper: {
                  sx: {
                    width: 'auto',
                  },
                },
              },
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.tooltipColor,
          borderRadius: tokens.radius.s,
          // MUI's default is 11px; 12px is the floor (design audit item 10).
          fontSize: '0.75rem',
          lineHeight: '140%',
        },
        arrow: {
          color: colors.tooltipColor,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        hover: {
          backgroundColor: colors.tableLineHoverColor,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: colors.textColor,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          '.MuiModal-backdrop': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  },
});
